import { Checkbox, FormControlLabel, Typography } from '@mui/material'
import { Box, LabraToolTip, PlanType } from '@labrav/react-components'
import React from 'react'
import { AddUpdateButton } from '../../../common/components/AddUpdateButton'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import { useStyles } from './PricingComponent.styles'
import { Installments } from '../../../modules/privateOffer/types'
import { KeyValue } from '../../../modules/privateOffer/privateOfferCreation/types'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import { useSelector } from '../../../store'
import clsx from 'clsx'
import { LoadingTypes, isLoading } from '@labrav/common'
import { InstallmentsRows } from './InstallmentsRows'
import {
  getInstallmentsKeyName,
  planTypesForCommonFields,
  pricingTooltipLabel,
} from './utils'

export type PricingComponentProps = {
  planType: PlanType
  flexiblePaymentTerms: boolean
  installments: Installments[]
  addInstallmentRows: (key: string) => void
  handlePricingCheckbox: (data: KeyValue) => void
  updateInstallments: (index: number, key: string) => (data: KeyValue) => void
  deleteInstallmentsRow: (index: number, key: string) => () => void
  errors: Array<Record<string, unknown>>
  isResale?: boolean
  heading: string
  subHeading: string
}

export const PricingComponent = React.memo(
  ({
    flexiblePaymentTerms,
    installments,
    addInstallmentRows,
    updateInstallments,
    deleteInstallmentsRow,
    handlePricingCheckbox,
    planType,
    errors,
    isResale = false,
    heading,
    subHeading,
  }: PricingComponentProps) => {
    const { classes } = useStyles()
    const installmentsKeyName = getInstallmentsKeyName(isResale)
    const handleChange =
      (key: string) =>
      (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        handlePricingCheckbox({ key: key, value: checked })
      }
    const addInstallmentRowsWithKey = () => {
      addInstallmentRows(installmentsKeyName)
    }
    const disabled = useSelector<boolean>(state =>
      isLoading(state.loading)(LoadingTypes.GENERAL)
    )

    return (
      <Box
        heading={heading}
        subHeading={subHeading}
        tooltipLabel={pricingTooltipLabel}
        icon={<InfoOutlined className={classes.infoIcon} />}
      >
        <div className={classes.pricingContainer} data-testid="pricing-div">
          {planTypesForCommonFields.includes(planType) && !isResale ? (
            <div className={classes.labelAndInfo}>
              <FormControlLabel
                label={
                  <div>
                    <Typography className={classes.formLabel}>
                      {'Flexible payment schedule'}
                    </Typography>
                  </div>
                }
                control={
                  <Checkbox
                    checked={flexiblePaymentTerms}
                    onChange={handleChange('flexiblePaymentTerms')}
                    key={'flexiblePaymentTerms'}
                    data-testid="flexible-pricing"
                    className={classes.checkbox}
                  />
                }
                className={classes.formLabelContainer}
              />
              <div className={classes.tooltipInfo}>
                <LabraToolTip
                  tooltipPlacement="right"
                  label="Setup payment installment by entering amount and date. Invoice will be generated on the dates mentioned with corresponding amount."
                >
                  <InfoOutlined className={classes.infoIcon} />
                </LabraToolTip>
              </div>
            </div>
          ) : (
            <div data-testid="empty-flexible-when-subscription"></div>
          )}
          {planTypesForCommonFields.includes(planType) &&
          flexiblePaymentTerms === true ? (
            <div
              className={clsx(
                classes.installmentContainer,
                isResale ? classes.resaleInstallmentContainer : ''
              )}
            >
              {installments.map((installment, index) => (
                <InstallmentsRows
                  key={`installment-${index}`}
                  installment={installment}
                  index={index}
                  updateInstallments={updateInstallments(
                    index,
                    installmentsKeyName
                  )}
                  deleteRow={deleteInstallmentsRow(index, installmentsKeyName)}
                  installmentsLength={installments.length}
                  flexiblePaymentTerms={flexiblePaymentTerms}
                  errors={errors[index]}
                />
              ))}
              <div className={classes.formLabelContainer}>
                <AddUpdateButton
                  label={'Add Installments'}
                  disabled={disabled}
                  type={'add-installment'}
                  icon={AddCircleOutlineIcon}
                  onClick={addInstallmentRowsWithKey}
                />
              </div>
            </div>
          ) : (
            <div data-testid="empty-installments-when-subscription"></div>
          )}
        </div>
      </Box>
    )
  }
)
